import { AlertCircle, ChevronLeft } from 'lucide-react';

import type { LandingPage, LandingPageStatus } from '@/lib/types';
import { LandingPageView } from './landing-page-view';
import { LandingPageGenerating } from './landing-page-generating';

// A generated landing page's status, as the Landing Pages list badges it (#6674).
export const LANDING_PAGE_STATUS_BADGE: Record<LandingPageStatus, { label: string; className: string }> = {
  GENERATING: { label: 'Generating', className: 'bg-[#FEF3C7] text-[#A16207]' },
  PREVIEW: { label: 'Preview', className: 'bg-[#EDE9FE] text-[#290BD9]' },
  PUBLISHED: { label: 'Live', className: 'bg-[#D1FAE5] text-[#047857]' },
  ERROR: { label: 'Error', className: 'bg-[#FEE2E2] text-[#B91C1C]' },
};

interface GeneratedLandingPageDetailProps {
  page: LandingPage;
  organizationId: number;
  onBack: () => void;
  onChanged: () => void;
}

/**
 * One generated page, open in full: its preview/publish/vibe-edit/leads view, or its generating
 * or error state. The list it opens from is the Landing Pages tab's own (#6674), which lists
 * generated pages beside the ones found on the site.
 */
export function GeneratedLandingPageDetail({ page, organizationId, onBack, onChanged }: GeneratedLandingPageDetailProps) {
  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm text-[#290BD9] hover:text-[#2309C5]"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to all pages
      </button>
      {page.status === 'GENERATING' ? (
        <LandingPageGenerating />
      ) : page.status === 'ERROR' ? (
        <div className="border-l-4 border-[#FCA5A5] bg-[#FEE2E2] text-[#B91C1C] p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold mb-1">Generation Failed</h3>
              <p className="text-sm">{page.error_detail ?? 'Landing page generation failed.'}</p>
            </div>
          </div>
        </div>
      ) : (
        <LandingPageView landingPage={page} organizationId={organizationId} onChanged={onChanged} />
      )}
    </div>
  );
}
